import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CALENDLY_FINANCE_SYNC_URL } from '../../../utils/FinancialDashboard';
import BackButton from '../../shared/BackButton';
import styles from './ScheduleCfpSessionPage.module.css';
import dashStyles from '../../Dashboard.module.css';
import CfpSegmentTabs from './CfpSegmentTabs';
import CfpTopicCard from './CfpTopicCard';
import CfpTopicCarousel from './CfpTopicCarousel';
import CfpSessionFooter from './CfpSessionFooter';
import CfpSessionListView from './CfpSessionListView';
import { fetchUserCfpSessionIsExisting, insertUserCfpSession } from '../../../utils/api';
import { useContentCatalog } from '../../../utils/contentCatalog';
import {
  defaultCfpSegmentId,
  getSegmentById,
  resolveSelectedCfpTopic,
} from './cfpSessionData';

export const SCHEDULE_CFP_SESSION_ROUTE = '/dashboard/schedule-cfp-session';

function InfoIcon() {
  return (
    <svg className={styles.infoBannerIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" />
      <path d="M12 10.5V16.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="12" cy="7.75" r="1.1" fill="currentColor" />
    </svg>
  );
}

function PaymentSuccessIcon() {
  return (
    <svg className={styles.paymentStatusIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M7.5 12.5L10.3 15.3L16.5 9"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PaymentCancelledIcon() {
  return (
    <svg className={styles.paymentStatusIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M9 9L15 15M15 9L9 15"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const PAYMENT_REDIRECT_DELAY_MS = 2000;

/* Stagger variants for the content sections */
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05,
    },
  },
};

const sectionVariants = {
  hidden: { opacity: 0, y: 22 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.48, ease: [0.16, 1, 0.3, 1] },
  },
};

function ScheduleCfpSessionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const calendarUrl =
    location.state?.calendarUrl?.trim() || CALENDLY_FINANCE_SYNC_URL;
  const expertId = location.state?.expertId?.trim() || 'expert-1';

  const { catalog, loading: catalogLoading, error: catalogError, retry: retryCatalog } = useContentCatalog();
  const topicsCatalog = catalog?.cfp_session_topics;
  const segments = useMemo(
    () => topicsCatalog?.segments || [],
    [topicsCatalog?.segments],
  );
  const maxSelections = Number(topicsCatalog?.max_selections) || 1;

  const [activeSegmentId, setActiveSegmentId] = useState('');
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);
  const [hasExistingSession, setHasExistingSession] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);

  useEffect(() => {
    if (!segments.length) return;
    setActiveSegmentId((current) => current || defaultCfpSegmentId(segments));
  }, [segments]);

  const activeSegment = useMemo(
    () => getSegmentById(segments, activeSegmentId),
    [segments, activeSegmentId],
  );
  const selectedCount = selectedTopicIds.length;
  const atMaxSelections = selectedCount >= maxSelections;

  const selectedTopic = useMemo(
    () => resolveSelectedCfpTopic(segments, selectedTopicIds, activeSegmentId),
    [segments, selectedTopicIds, activeSegmentId],
  );

  const openCalendar = useCallback(() => {
    window.location.assign(calendarUrl);
  }, [calendarUrl]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const checkExisting = async () => {
      setIsCheckingExisting(true);
      const result = await fetchUserCfpSessionIsExisting(navigate, controller.signal);
      if (controller.signal.aborted) return;

      const existing = result?.isExisting === true;
      setHasExistingSession(existing);
      setShowScheduleForm(!existing);
      setIsCheckingExisting(false);
    };

    checkExisting();
    return () => controller.abort();
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get('payment');
    if (payment === 'success' || payment === 'cancelled') {
      setPaymentStatus(payment);
      navigate(location.pathname, { replace: true, state: location.state });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!paymentStatus) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const timer = setTimeout(() => {
      if (paymentStatus === 'success') {
        openCalendar();
      }
      setPaymentStatus(null);
    }, PAYMENT_REDIRECT_DELAY_MS);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = previousOverflow;
    };
  }, [paymentStatus, openCalendar]);

  const handleBack = useCallback(() => {
    if (hasExistingSession && showScheduleForm) {
      setShowScheduleForm(false);
      return;
    }
    navigate(-1);
  }, [navigate, hasExistingSession, showScheduleForm]);

  const handleAddNew = useCallback(() => {
    setShowScheduleForm(true);
    window.scrollTo(0, 0);
  }, []);

  const handleSegmentChange = useCallback((segmentId) => {
    setActiveSegmentId(segmentId);
  }, []);

  const handleTopicToggle = useCallback((topicId) => {
    setSelectedTopicIds((prev) => {
      if (prev.includes(topicId)) return [];
      return [topicId];
    });
  }, []);

  const handleTopicSelect = useCallback((topicId) => {
    setSelectedTopicIds([topicId]);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (selectedTopicIds.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    const result = await insertUserCfpSession(
      {
        tabLabel: selectedTopic?.tabLabel,
        topicCardTitle: selectedTopic?.topicCardTitle,
        expertId,
      },
      navigate,
    );
    setIsSubmitting(false);

    if (result.ok) {
      openCalendar();
    }
  }, [selectedTopicIds, selectedTopic, isSubmitting, navigate, openCalendar, expertId]);

  const showListView = hasExistingSession && !showScheduleForm;

  return (
    <div className={styles.page}>
      <AnimatePresence>
        {paymentStatus && (
          <motion.div
            className={styles.paymentOverlay}
            role="alertdialog"
            aria-live="assertive"
            aria-modal="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.preventDefault()}
          >
            <motion.div
              className={styles.paymentOverlayCard}
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            >
              {paymentStatus === 'success' ? (
                <div
                  className={`${styles.paymentStatusIconWrap} ${styles.paymentStatusIconWrapSuccess}`}
                >
                  <PaymentSuccessIcon />
                </div>
              ) : (
                <div
                  className={`${styles.paymentStatusIconWrap} ${styles.paymentStatusIconWrapCancelled}`}
                >
                  <PaymentCancelledIcon />
                </div>
              )}

              <h2 className={styles.paymentStatusTitle}>
                {paymentStatus === 'success' ? 'Payment Successful' : 'Payment Cancelled'}
              </h2>
              <p className={styles.paymentStatusMessage}>
                {paymentStatus === 'success'
                  ? 'Redirecting you to your session calendar…'
                  : "Your payment wasn't completed. Redirecting you back…"}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <header className={dashStyles.headerBar}>
        <div className={dashStyles.headerLeft}>
          <BackButton className={dashStyles.headerBackButton} onClick={handleBack} aria-label="Go back" />
          <div className={dashStyles.headerTitleGroup}>
            <h1 className={dashStyles.headerTitle}>
              {showListView ? 'Your CFP Sessions' : 'Schedule Your CFP Session'}
            </h1>
          </div>
        </div>
      </header>

      {isCheckingExisting || (showScheduleForm && catalogLoading) ? (
        <main className={styles.main}>
          <div className={styles.container}>
            <div className={styles.listLoading} aria-busy="true" aria-live="polite">
              Loading…
            </div>
          </div>
        </main>
      ) : showScheduleForm && (catalogError || !segments.length) ? (
        <main className={styles.main}>
          <div className={styles.container}>
            <p role="alert">{catalogError || 'Session topics are unavailable right now.'}</p>
            <button type="button" onClick={retryCatalog}>Try again</button>
          </div>
        </main>
      ) : showListView ? (
        <CfpSessionListView navigate={navigate} onAddNew={handleAddNew} />
      ) : (
        <>
      {/* Main content with staggered sections */}
      <main className={styles.main}>
        <motion.div
          className={styles.container}
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          {/* Hero description */}
          <motion.section className={styles.hero} variants={sectionVariants}>
            <p className={styles.heroDescription}>
              {topicsCatalog?.intro}
            </p>
          </motion.section>

          {/* Info banner */}
          <motion.div className={styles.infoBanner} role="note" variants={sectionVariants}>
            <InfoIcon />
            <div className={styles.infoBannerContent}>
              <p className={styles.infoBannerText}>
                {topicsCatalog?.selection_prompt}
              </p>
              <p className={styles.infoBannerSubtext}>{topicsCatalog?.selection_note}</p>
            </div>
          </motion.div>

          {/* Segment tabs */}
          <motion.div variants={sectionVariants}>
            <CfpSegmentTabs
              segments={segments}
              activeSegmentId={activeSegmentId}
              onSegmentChange={handleSegmentChange}
            />
          </motion.div>

          {/* Topic carousel — swipeable row, animates in/out on segment change */}
          <motion.div variants={sectionVariants}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSegmentId}
                id={`cfp-tabpanel-${activeSegmentId}`}
                role="tabpanel"
                aria-labelledby={`cfp-tab-${activeSegmentId}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              >
                <CfpTopicCarousel segmentId={activeSegmentId}>
                  {(activeSegment?.topics || []).map((topic, index) => (
                    <CfpTopicCard
                      key={topic.id}
                      topic={topic}
                      index={index}
                      selected={selectedTopicIds.includes(topic.id)}
                      disabled={atMaxSelections && !selectedTopicIds.includes(topic.id)}
                      onToggle={handleTopicToggle}
                      onSelect={handleTopicSelect}
                    />
                  ))}
                </CfpTopicCarousel>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </main>

      <CfpSessionFooter
        selectedCount={selectedCount}
        selectedTopic={selectedTopic}
        expertId={expertId}
        isSubmitting={isSubmitting}
        navigate={navigate}
        onConfirm={handleConfirm}
      />
        </>
      )}
    </div>
  );
}

export default ScheduleCfpSessionPage;
